// Lectura de tablas desde CSV, texto pegado desde Excel (TSV) y archivos .xlsx, sin dependencias externas.
// Devuelve siempre un arreglo de filas; cada fila es un arreglo de textos.

export function parseDelimited(input) {
  const text = String(input || '').replace(/^\uFEFF/, '')
  const firstLine = text.split(/\r?\n/).find((l) => l.trim()) || ''
  const counts = { '\t': 0, ';': 0, ',': 0 }
  let q = false
  for (const ch of firstLine) {
    if (ch === '"') q = !q
    else if (!q && ch in counts) counts[ch]++
  }
  const delimiter = counts['\t'] > 0 ? '\t' : counts[';'] > counts[','] ? ';' : ','

  const rows = []
  let row = []
  let cell = ''
  let inQuotes = false
  const pushCell = () => { row.push(cell.trim()); cell = '' }
  const pushRow = () => { pushCell(); if (row.some((c) => c !== '')) rows.push(row); row = [] }

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++ }
      else if (ch === '"') inQuotes = false
      else cell += ch
    } else if (ch === '"') inQuotes = true
    else if (ch === delimiter) pushCell()
    else if (ch === '\n') pushRow()
    else if (ch === '\r') { /* se ignora; el salto real es \n */ }
    else cell += ch
  }
  if (cell !== '' || row.length) pushRow()
  return rows
}

// ---------- .xlsx (zip + XML) ----------

async function inflateRaw(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

async function unzip(buffer) {
  const view = new DataView(buffer)
  const bytes = new Uint8Array(buffer)
  let eocd = -1
  for (let i = buffer.byteLength - 22; i >= Math.max(0, buffer.byteLength - 65557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break }
  }
  if (eocd < 0) throw new Error('El archivo no es un Excel (.xlsx) válido')
  const count = view.getUint16(eocd + 10, true)
  let offset = view.getUint32(eocd + 16, true)
  const dec = new TextDecoder()
  const entries = {}
  for (let n = 0; n < count; n++) {
    if (view.getUint32(offset, true) !== 0x02014b50) break
    const method = view.getUint16(offset + 10, true)
    const compSize = view.getUint32(offset + 20, true)
    const nameLen = view.getUint16(offset + 28, true)
    const extraLen = view.getUint16(offset + 30, true)
    const commentLen = view.getUint16(offset + 32, true)
    const localOffset = view.getUint32(offset + 42, true)
    const name = dec.decode(bytes.subarray(offset + 46, offset + 46 + nameLen))
    const localNameLen = view.getUint16(localOffset + 26, true)
    const localExtraLen = view.getUint16(localOffset + 28, true)
    const start = localOffset + 30 + localNameLen + localExtraLen
    entries[name] = { method, data: bytes.subarray(start, start + compSize) }
    offset += 46 + nameLen + extraLen + commentLen
  }
  const read = async (name) => {
    const e = entries[name]
    if (!e) return null
    const raw = e.method === 0 ? e.data : await inflateRaw(e.data)
    return dec.decode(raw)
  }
  return { names: Object.keys(entries), read }
}

const parseXml = (xml) => new DOMParser().parseFromString(xml, 'application/xml')
const colIndex = (ref) => {
  const letters = (ref.match(/^[A-Z]+/) || ['A'])[0]
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

export async function parseXlsx(buffer) {
  if (typeof DecompressionStream === 'undefined') throw new Error('Tu navegador no puede leer archivos Excel. Guarda el archivo como CSV o pega las celdas')
  const zip = await unzip(buffer)

  // Primera hoja: la que apunta el libro; si no se puede resolver, la primera hoja encontrada.
  let sheetPath = null
  const wb = await zip.read('xl/workbook.xml')
  const rels = await zip.read('xl/_rels/workbook.xml.rels')
  if (wb && rels) {
    const firstSheet = parseXml(wb).getElementsByTagName('sheet')[0]
    const rid = firstSheet && (firstSheet.getAttribute('r:id') || firstSheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id'))
    const rel = Array.from(parseXml(rels).getElementsByTagName('Relationship')).find((r) => r.getAttribute('Id') === rid)
    if (rel) {
      const target = rel.getAttribute('Target')
      sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target}`
    }
  }
  if (!sheetPath || !zip.names.includes(sheetPath)) {
    sheetPath = zip.names.filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n)).sort()[0]
  }
  if (!sheetPath) throw new Error('No se encontró ninguna hoja en el archivo')

  const shared = []
  const ss = await zip.read('xl/sharedStrings.xml')
  if (ss) {
    for (const si of Array.from(parseXml(ss).getElementsByTagName('si'))) {
      shared.push(Array.from(si.getElementsByTagName('t')).map((t) => t.textContent).join(''))
    }
  }

  const sheet = parseXml(await zip.read(sheetPath))
  const rows = []
  for (const r of Array.from(sheet.getElementsByTagName('row'))) {
    const cells = []
    for (const c of Array.from(r.getElementsByTagName('c'))) {
      const idx = colIndex(c.getAttribute('r') || '')
      const type = c.getAttribute('t')
      const v = c.getElementsByTagName('v')[0]
      let value = ''
      if (type === 's' && v) value = shared[Number(v.textContent)] ?? ''
      else if (type === 'inlineStr') value = Array.from(c.getElementsByTagName('t')).map((t) => t.textContent).join('')
      else if (v) value = v.textContent
      cells[idx] = String(value).trim()
    }
    const filled = Array.from(cells, (x) => x ?? '')
    if (filled.some((x) => x !== '')) rows.push(filled)
  }
  return rows
}

export async function readTableFile(file) {
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.xlsx') || name.endsWith('.xlsm')) return parseXlsx(await file.arrayBuffer())
  if (name.endsWith('.xls')) throw new Error('El formato .xls antiguo no se puede leer. Ábrelo en Excel y guárdalo como .xlsx o CSV')
  const buffer = await file.arrayBuffer()
  let text = new TextDecoder('utf-8').decode(buffer)
  if (text.includes('�')) text = new TextDecoder('windows-1252').decode(buffer)
  return parseDelimited(text)
}